import {
  Children,
  CommonProps,
  LocalizedString,
} from "@dbn/omnichannel-design-system-g4p/lib//util";
import {
  Box,
  containerRowMaxWidthG4P as containerRowMaxWidth,
  LayoutContext,
  Space,
  spaceUnitG4P as spaceUnit,
  Sticky,
  useBreakpoints,
  useDeviceUA,
  useIsMobileLayout,
  useIsTouchScreen,
} from "@dbn/omnichannel-design-system-g4p";
import { Option } from "fp-ts/Option";
import { Stepper } from "./Stepper";
import * as classes from "./MainLayout.css";
import { constant, constNull, pipe } from "fp-ts/function";
import { boolean, option } from "fp-ts";
import { Footer } from "./Footer";
import cx from "classnames";
import { ContainerRowB } from "./grid/ContainerRowB";
import { Steps } from "@g4p/react-styleguide-library";
import { HeaderCZA11Y } from "./HeaderCZA11Y";
import "./steps.css";
import { useEffect, useMemo, useState } from "react";
import { useFormatMessage } from "../../intl";
import { AccessibilityFocusManager } from "../../AccessibilityFocusManager";

type HeaderProps = React.ComponentProps<typeof HeaderCZA11Y>;

type Props = CommonProps & {
  steps: Option<React.ComponentProps<typeof Stepper>["steps"]>;
  children: Children;
  title: HeaderProps["title"];
  subtitle?: HeaderProps["subtitle"];
  remainingTime?: HeaderProps["remainingTime"];
  centerContent?: HeaderProps["centerContent"];
  backgroundImageURL: Option<string>;
  onLogoClick: HeaderProps["onLogoClick"];
  onStepClick: (index: number) => unknown;
  iconAction: HeaderProps["iconAction"];
  dropdownHeading?: LocalizedString;
  contentBackground?: string;
  footer: Option<LocalizedString>;
  hideHeaderOnMobile?: boolean;
  isStepClickable?: (index: number) => boolean;
  desktopContentLayoutHeight?: string;
  desktopContentWrapperWidth?: string;
};

type StepStatus = "wait" | "process" | "finish";

export function MainLayoutCZ(props: Props) {
  const isMobileLayout = useIsMobileLayout();
  const isTouchScreen = useIsTouchScreen();
  const { foldBreakpoints } = useBreakpoints();
  const [verticalStepsLayoutTrigger, updateState] = useState<unknown>();
  const formatMessage = useFormatMessage();
  const { isIOS } = useDeviceUA();

  const paddingX = isTouchScreen
    ? 0
    : foldBreakpoints({
        ifDesktopWide: constant("10%"),
        ifDesktop: constant("10%"),
        ifTablet: constant("10%"),
        ifMobileLandscape: constant("8%"),
        ifMobilePortrait: constant("6%"),
      });
  const stepsArray = option.isSome(props.steps)
    ? [
        ...props.steps.value.lefts.map(title => ({
          title: capitalizeFirstLowerRest(title),
          status: "finish" as StepStatus,
        })),
        {
          title: capitalizeFirstLowerRest(props.steps.value.focus),
          status: "process" as StepStatus,
        },
        ...props.steps.value.rights.map(title => ({
          title: capitalizeFirstLowerRest(title),
          status: "wait" as StepStatus,
        })),
      ]
    : undefined;

  const hasSteps = stepsArray !== undefined && stepsArray.length > 1;
  const showVerticalSteps = stepsArray !== undefined && stepsArray.length > 6;

  useEffect(() => {
    updateState({});
  }, [showVerticalSteps]);

  const [headerLeftMarginPx, setHeaderLeftMarginPx] = useState(0);

  useEffect(() => {
    if (showVerticalSteps) {
      const verticalStepsContainer = document.getElementById(
        "verticalStepsContainer"
      );
      if (verticalStepsContainer && verticalStepsContainer.clientWidth > 0) {
        setHeaderLeftMarginPx(verticalStepsContainer.clientWidth);
      }
    } else {
      setHeaderLeftMarginPx(0);
    }
  }, [verticalStepsLayoutTrigger, showVerticalSteps]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const closeBtn = document.querySelector(
        ".ant-drawer .drawer-header-container .close-icon-container"
      );
      if (closeBtn) {
        closeBtn.setAttribute("aria-label", formatMessage("Modal.closeButton"));
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  const header = (
    <HeaderCZA11Y
      dropdownHeading={pipe(props.dropdownHeading, option.fromNullable)}
      title={props.title}
      subtitle={props.subtitle}
      remainingTime={props.remainingTime}
      onLogoClick={props.onLogoClick}
      iconAction={props.iconAction}
      centerContent={props.centerContent}
      headerLeftMarginPx={headerLeftMarginPx}
    />
  );

  const footer = pipe(
    props.footer,
    option.fold(constNull, footer => <Footer>{footer}</Footer>)
  );

  function capitalizeFirstLowerRest(str: string) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const horizontalSteps = useMemo(() => {
    return (
      <Steps
        items={stepsArray}
        current={stepsArray?.findIndex(step => step.status === "process")}
        compressed={isMobileLayout}
        mobileQuickIconAriaLabel={formatMessage("ShowAllSteps.ariaLabel")}
        isDrawerOnMobile
        showFullWidth
      />
    );
  }, [stepsArray, isMobileLayout]);

  const verticalSteps = useMemo(() => {
    return (
      <Steps
        items={stepsArray}
        compressed={false}
        current={stepsArray?.findIndex(step => step.status === "process")}
        direction={"vertical"}
        mobileQuickIconAriaLabel={formatMessage("ShowAllSteps.ariaLabel")}
        isDrawerOnMobile
        showFullWidth
      />
    );
  }, [stepsArray]);

  const desktopLayoutForVertical = (
    <Box column grow>
      {header}
      <Space units={6} />
      <Box style={{ justifyContent: hasSteps ? "center" : undefined }}>
        {stepsArray !== undefined ? (
          <Box
            style={{
              // paddingLeft: "10%",
              // marginLeft: hasSteps ? "-15%" : undefined,
              // maxWidth: containerRowMaxWidth,
              maxWidth: "15%",
              // justifyContent: "space-around",
            }}
          >
            <Box
              column
              grow
              shrink
              id={"verticalStepsContainer"}
              style={{
                // paddingLeft: paddingX,
                paddingTop: 10 * spaceUnit,
              }}
            >
              {verticalSteps}
            </Box>
          </Box>
        ) : (
          <></>
        )}
        {pipe(
          props.backgroundImageURL,
          option.map(url => (
            <ContainerRowB className={classes.containerRowB} imageUrl={url} />
          )),
          option.toNullable
        )}

        <Box
          column
          grow
          shrink
          width={"100%"}
          id={"snackbarContainer"}
          style={{
            maxWidth: `min(${containerRowMaxWidth}px, 80%)`,
            position: "relative",
          }}
        >
          <Box
            column
            grow
            shrink
            className={cx(classes.desktopContentVerticalSteps, {
              [classes.touchScreenContainer]: isTouchScreen,
            })}
          >
            {props.children}
          </Box>
          {footer}
        </Box>
      </Box>
    </Box>
  );

  const renderHorizontalStepper = (
    <Box column grow shrink>
      {horizontalSteps}
    </Box>
  );

  const desktopLayoutForHorizontal = (
    <Box column grow style={{ height: props.desktopContentLayoutHeight }}>
      {header}
      <Space units={6} />
      <Box
        column
        grow
        shrink
        id={"snackbarContainer"}
        style={{
          position: "relative",
          maxWidth: hasSteps
            ? undefined
            : `min(${containerRowMaxWidth}px, 80%)`,
          width: hasSteps
            ? `min(${containerRowMaxWidth}px, 80%)`
            : props.desktopContentWrapperWidth !== undefined
            ? props.desktopContentWrapperWidth
            : "100%",
          alignSelf: "center",
        }}
      >
        {hasSteps ? renderHorizontalStepper : <></>}
        {pipe(
          props.backgroundImageURL,
          option.map(url => (
            <ContainerRowB className={classes.containerRowB} imageUrl={url} />
          )),
          option.toNullable
        )}
        <Box
          column
          grow
          shrink
          className={cx(classes.desktopContent, {
            [classes.touchScreenContainer]: isTouchScreen,
          })}
        >
          {props.children}
        </Box>
        {footer}
      </Box>
    </Box>
  );

  const mobileLayout = (
    <Box column grow className={classes.mobileContainer}>
      {isIOS && (
        <AccessibilityFocusManager
          currentStep={stepsArray?.findIndex(step => step.status === "process")}
        />
      )}
      {!props.hideHeaderOnMobile && header}
      {stepsArray !== undefined ? (
        <Sticky
          className={classes.macroStepperContainer}
          top={0}
          style={{ zIndex: 2 }}
        >
          <Box
            width="100%"
            id={"snackbarContainer"}
            style={{
              maxWidth: containerRowMaxWidth,
              margin: "0 auto",
              justifyContent: "space-around",
              position: "relative",
            }}
          >
            <Box
              column
              grow
              shrink
              style={
                !isMobileLayout
                  ? { paddingLeft: paddingX, paddingRight: paddingX }
                  : undefined
              }
            >
              <div
                tabIndex={0}
                aria-hidden={true}
                style={{
                  width: "0",
                  height: "0",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
              {horizontalSteps}
            </Box>
          </Box>
        </Sticky>
      ) : (
        <></>
      )}
      <Box column grow shrink className={classes.mobileContent}>
        {props.children}
      </Box>
      {footer}
    </Box>
  );

  return (
    <LayoutContext.Provider
      value={{
        hasStepper: option.isSome(props.steps),
        hasSidebar: false,
      }}
    >
      {pipe(
        isMobileLayout,
        boolean.fold(
          () =>
            showVerticalSteps
              ? desktopLayoutForVertical
              : desktopLayoutForHorizontal,
          () => mobileLayout
        )
      )}
    </LayoutContext.Provider>
  );
}
